import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import '@components/Admin/admin.css'
import '@assets/styles/bbd.less'

import { useUsers } from '@api/useUsers'
import { responseErrorHandler } from '@api/utils'
import { APILinks } from '@components/_SiteWide/UploadStatus/APILinks'
import { LoginScreen, useThemeContext } from '@gravitate-js/excalibrr'
import { GlobalAPIProvider } from '@gravitate-js/the-armory'
import api from '@utils/api'
import {
  getCurrentMarket,
  getDisplayTimezoneString,
  getShowNetworkTimezone,
  setDisplayTimezoneString,
  setShowNetworkTimezone,
} from '@utils/general'
import { useEffect } from 'react'
import ReactGA from 'react-ga'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'

import { Default } from './Default'
import { SecurityContextProvider, useSecurityContext } from './SecurityContext'
import { useUserSettings } from '@hooks/settings'
import SSOAuth from '@components/_SiteWide/SSOAuth'
import { FastBackwardFilled } from '@ant-design/icons'
import { findPageTitle, pageConfig } from './_pageConfig'

ReactGA.initialize('UA-218772036-1')
export function Main() {
  const navigate = useNavigate()
  const location = useLocation()
  useEffect(() => {
    api.setHandleLogout(() => navigate('/login'))
  }, [])

  useEffect(() => {
    document.title = findPageTitle(location.pathname, pageConfig)
  }, [location.pathname])

  return (
    <SecurityContextProvider>
      <AppRoutes />
    </SecurityContextProvider>
  )
}
function AppRoutes() {
  const { handleLogout } = useSecurityContext()

  return (
    // add errorhandler once written here.
    <GlobalAPIProvider
      errorHandler={(resp) => responseErrorHandler(resp)}
      logoutCallback={handleLogout}
      defaultParams={getCurrentMarket() && { market: getCurrentMarket() }}
    >
      <Routes>
        <Route path='/login' exact element={<SiteLogin />} />
        <Route path='/healthz' exact element={<h3>Hey there. The app is healthy</h3>} />
        <Route path='/api' exact element={<APILinks />} />
        <Route path='/ssoAuth' exact element={<SSOAuth />} />
        <Route path='/*' element={api.isLoggedIn() ? <Default /> : <Navigate to='/login' />} />
      </Routes>
    </GlobalAPIProvider>
  )
}

function SiteLogin() {
  const { setScopes } = useSecurityContext()
  const { setupSettings, getSetting } = useUserSettings()
  const navigate = useNavigate()
  const { isDarkTheme } = useThemeContext()

  const { sendResetEmailRequest, resetPasswordMutation, validatePasswordRequest, validateOTPRequest } = useUsers()
  const handlePasswordReset = (one_time_password, new_password) => {
    return resetPasswordMutation.mutateAsync({
      one_time_password,
      new_password,
    })
  }
  const handleValidate = async ({ username, password }) => {
    const fetchedData = await validatePasswordRequest.mutateAsync({
      username,
      password,
    })
    const prom = new Promise((resolve) => resolve({ data: fetchedData }))
    return prom
  }
  return (
    <LoginScreen
      loginTitle={
        <>
          BEST <b>PLAN.</b> BEST <b>ROUTE.</b> BEST <b>BUY.</b>
        </>
      }
      onLogin={(values) => {
        api.authenticate(values, setScopes, setupSettings).then((isLoggedIn) => {
          let shouldReload = FastBackwardFilled
          if (isLoggedIn) {
            navigate('/')
            getSetting('USE_NETWORK_TIMEZONE').then((showNetworkTimezone) => {
              shouldReload = getShowNetworkTimezone() !== showNetworkTimezone
              setShowNetworkTimezone(showNetworkTimezone)
            })
            getSetting('SHOW_TIMEZONE_STRING').then((showTimezoneString) => {
              shouldReload = getDisplayTimezoneString() !== showTimezoneString
              setDisplayTimezoneString(showTimezoneString)
            })
            getSetting('TYPE_OF_THEME').then((theme) => {
              if (theme === 'DARK_MODE' && !isDarkTheme) {
                shouldReload = true
                localStorage.setItem('TYPE_OF_THEME', theme)
              } else if (theme === 'LIGHT_MODE' && isDarkTheme) {
                shouldReload = true
                localStorage.setItem('TYPE_OF_THEME', theme)
              }
            })
            if (shouldReload) window.location.reload()
          }
        })
      }}
      validateOTP={validateOTPRequest.mutateAsync}
      validatePasswordEP={handleValidate}
      resetPassword={handlePasswordReset}
      sendResetEmail={sendResetEmailRequest.mutateAsync}
    />
  )
}
